import { inject } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, ValidationErrors, ValidatorFn } from '@angular/forms';
import { catchError, concatMap, forkJoin, from, map, Observable, of, timeout, toArray } from 'rxjs';
import { ComparisonType, VideoValidationService } from './services/video-validation.service';

import { logger } from '../helpers/logger';
export interface ValidatorSet {
  name: string;
  validators: AsyncValidatorFn[];
}

export class CustomValidators {
  /**
   * Creates a video set validator that accepts multiple validator sets.
   * The validation passes if ANY of the validator sets pass completely (all validators in that set pass).
   * @param validatorSets Array of named validator sets to check
   * @returns AsyncValidatorFn that validates against multiple video parameter sets with OR logic
   */
  public static videoSetValidator(validatorSets: ValidatorSet[]): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const value = control.value;

      // Handle empty values
      if (!value) {
        return of(null);
      }

      // Handle both single File and FileList
      let filesToValidate: File[] = [];

      if (value instanceof FileList) {
        // Convert FileList to array
        filesToValidate = Array.from(value);
      } else if (value instanceof File) {
        // Single file
        filesToValidate = [value];
      } else {
        // Unsupported value type
        return of(null);
      }

      // Only validate MP4 files
      const mp4Files = filesToValidate.filter(file => file.type === 'video/mp4');

      if (mp4Files.length === 0) {
        return of(null);
      }

      // Process each file
      return from(mp4Files).pipe(
        concatMap((file: File) => {
          // Test the file against each validator set
          const setValidations = validatorSets.map(set => {
            // Create a temporary control for this file
            const tempControl = {
              value: file,
            } as AbstractControl;

            // Run all validators in this set and combine their results
            const validatorObservables = set.validators.map(validator => validator(tempControl));

            // Use forkJoin to run all validators in parallel and wait for all to complete
            return forkJoin(validatorObservables).pipe(
              map(results => {
                // Check if all validators in this set passed (returned null)
                const allValid = results.every(result => result === null);

                return {
                  setName: set.name,
                  valid: allValid,
                  errors: allValid ? null : results.filter(r => r !== null),
                };
              }),
              catchError(error => {
                logger.error(`Error validating against set "${set.name}":`, error);
                return of({
                  setName: set.name,
                  valid: false,
                  errors: [{ error: 'Validation error occurred' }],
                });
              })
            );
          });

          // Run all validator sets and check if any pass
          return forkJoin(setValidations).pipe(
            map(setResults => ({
              file,
              setResults,
            }))
          );
        }),
        toArray(),
        map(fileResults => {
          // Check if all files passed at least one validator set
          const invalidFiles = fileResults.filter(fileResult => {
            // A file is valid if ANY set was valid
            return !fileResult.setResults.some(setResult => setResult.valid);
          });

          if (invalidFiles.length === 0) {
            return null; // All files passed at least one set
          }

          // Build detailed error messages
          const errors: ValidationErrors = {
            orValidation: {
              files: invalidFiles.map(fileResult => {
                const failedSets = fileResult.setResults
                  .filter(setResult => !setResult.valid)
                  .map(setResult => {
                    // Extract error messages from each failed validator in the set
                    const errorMessages: string[] = [];

                    if (setResult.errors) {
                      setResult.errors.forEach((error: any) => {
                        if (error) {
                          // Extract messages from different error types
                          if (error.invalidBitrate) {
                            errorMessages.push(...error.invalidBitrate.files.map((f: any) => f.message));
                          }
                          if (error.invalidFramerate) {
                            errorMessages.push(...error.invalidFramerate.files.map((f: any) => f.message));
                          }
                          if (error.invalidResolution) {
                            errorMessages.push(...error.invalidResolution.files.map((f: any) => f.message));
                          }
                        }
                      });
                    }

                    return {
                      setName: setResult.setName,
                      errors: errorMessages,
                    };
                  });

                return {
                  name: fileResult.file.name,
                  message: `Video does not match any accepted format`,
                  failedSets,
                };
              }),
            },
          };

          return errors;
        })
      );
    };
  }

  /**
   * Creates an async validator function for checking video framerate
   * @param allowedFramerates Array of allowed framerates (e.g. [30, 60])
   * @returns AsyncValidatorFn that validates video files have one of the allowed framerates
   */
  public static videoFramerateValidator(allowedFramerates: number[]): AsyncValidatorFn {
    const videoValidationService = inject(VideoValidationService);

    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const value = control.value;

      // Handle empty values
      if (!value) {
        return of(null);
      }

      // Handle both single File and FileList
      let filesToValidate: File[] = [];

      if (value instanceof FileList) {
        // Convert FileList to array
        filesToValidate = Array.from(value);
      } else if (value instanceof File) {
        // Single file
        filesToValidate = [value];
      } else {
        // Unsupported value type
        return of(null);
      }

      // Only validate MP4 files
      const mp4Files = filesToValidate.filter(file => file.type === 'video/mp4');

      if (mp4Files.length === 0) {
        return of(null);
      }

      // Define the type for validation results
      type ValidationResult = {
        file: File;
        result: {
          valid: boolean;
          message?: string;
          framerate?: number;
        };
      };

      const filesToProcess = mp4Files;

      // Process files sequentially with concurrency control
      return from(filesToProcess).pipe(
        // Use concatMap to process one file at a time
        concatMap((file: File) =>
          // Add timeout to prevent long-running operations
          from(videoValidationService.validateVideoFramerate(file, allowedFramerates)).pipe(
            timeout(10000), // 10 second timeout per file
            map(result => ({ file, result })),
            catchError(error => {
              logger.error('Error validating file:', file.name, error);
              return of({
                file,
                result: {
                  valid: false,
                  message:
                    error.name === 'TimeoutError'
                      ? 'Validation timed out - file may be too large'
                      : 'Error validating video framerate',
                },
              });
            })
          )
        ),
        // Collect all results
        toArray(),
        map((results: ValidationResult[]) => {
          const invalidResults = results.filter(item => !item.result.valid);

          if (invalidResults.length === 0) {
            return null;
          }

          // Create error object with details for each invalid file
          const errors: ValidationErrors = {
            invalidFramerate: {
              files: invalidResults.map(item => ({
                name: item.file.name,
                message: item.result.message,
                framerate: item.result.framerate,
                allowedFramerates: allowedFramerates,
              })),
            },
          };

          return errors;
        })
      );
    };
  }

  /**
   * Creates an async validator function for checking video resolution
   * @param width The width to compare against
   * @param height The height to compare against
   * @param comparisonType The type of comparison to perform (less than, less than or equal, exactly, greater than or equal, greater than)
   * @returns AsyncValidatorFn that validates video files have the specified resolution
   */
  public static videoResolutionValidator(
    width: number,
    height: number,
    comparisonType: ComparisonType = ComparisonType.Exact
  ): AsyncValidatorFn {
    const videoValidationService = inject(VideoValidationService);

    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const value = control.value;

      // Handle empty values
      if (!value) {
        return of(null);
      }

      // Handle both single File and FileList
      let filesToValidate: File[] = [];

      if (value instanceof FileList) {
        // Convert FileList to array
        filesToValidate = Array.from(value);
      } else if (value instanceof File) {
        // Single file
        filesToValidate = [value];
      } else {
        // Unsupported value type
        return of(null);
      }

      // Only validate MP4 files
      const mp4Files = filesToValidate.filter(file => file.type === 'video/mp4');

      if (mp4Files.length === 0) {
        return of(null);
      }

      // Define the type for validation results
      type ValidationResult = {
        file: File;
        result: {
          valid: boolean;
          message?: string;
          width?: number;
          height?: number;
        };
      };

      const filesToProcess = mp4Files;

      // Process files sequentially with concurrency control
      return from(filesToProcess).pipe(
        // Use concatMap to process one file at a time
        concatMap((file: File) =>
          // Add timeout to prevent long-running operations
          from(videoValidationService.validateVideoResolution(file, width, height, comparisonType)).pipe(
            timeout(10000), // 10 second timeout per file
            map(result => ({ file, result })),
            catchError(error => {
              logger.error('Error validating file:', file.name, error);
              return of({
                file,
                result: {
                  valid: false,
                  message:
                    error.name === 'TimeoutError'
                      ? 'Validation timed out - file may be too large'
                      : 'Error validating video resolution',
                },
              });
            })
          )
        ),
        // Collect all results
        toArray(),
        map((results: ValidationResult[]) => {
          const invalidResults = results.filter(item => !item.result.valid);

          if (invalidResults.length === 0) {
            return null;
          }

          // Create error object with details for each invalid file
          const errors: ValidationErrors = {
            invalidResolution: {
              files: invalidResults.map(item => ({
                name: item.file.name,
                message: item.result.message,
                width: item.result.width,
                height: item.result.height,
                requiredWidth: width,
                requiredHeight: height,
                comparisonType: comparisonType,
              })),
            },
          };

          return errors;
        })
      );
    };
  }

  /**
   * Creates an async validator function for checking video bitrate
   * @param bitrateMbps The bitrate value in Mbps to compare against
   * @param comparisonType The type of comparison to perform (less than, less than or equal, exactly, greater than or equal, greater than)
   * @param errorMarginMbps The error margin in Mbps to allow for comparison (default: 0)
   * @returns AsyncValidatorFn that validates video files meet the bitrate criteria
   */
  public static videoBitrateValidator(
    bitrateMbps: number,
    comparisonType: ComparisonType = ComparisonType.Exact,
    errorMarginMbps: number = 0
  ): AsyncValidatorFn {
    const videoValidationService = inject(VideoValidationService);

    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const value = control.value;

      // Handle empty values
      if (!value) {
        return of(null);
      }

      // Handle both single File and FileList
      let filesToValidate: File[] = [];

      if (value instanceof FileList) {
        // Convert FileList to array
        filesToValidate = Array.from(value);
      } else if (value instanceof File) {
        // Single file
        filesToValidate = [value];
      } else {
        // Unsupported value type
        return of(null);
      }

      // Only validate MP4 files
      const mp4Files = filesToValidate.filter(file => file.type === 'video/mp4');

      if (mp4Files.length === 0) {
        return of(null);
      }

      // Define the type for validation results
      type ValidationResult = {
        file: File;
        result: {
          valid: boolean;
          message?: string;
          bitrate?: number;
        };
      };

      const filesToProcess = mp4Files;

      // Process files sequentially with concurrency control
      return from(filesToProcess).pipe(
        // Use concatMap to process one file at a time
        concatMap((file: File) =>
          // Add timeout to prevent long-running operations
          from(videoValidationService.validateVideoBitrate(file, bitrateMbps, comparisonType, errorMarginMbps)).pipe(
            timeout(10000), // 10 second timeout per file
            map(result => ({ file, result })),
            catchError(error => {
              logger.error('Error validating file:', file.name, error);
              return of({
                file,
                result: {
                  valid: false,
                  message:
                    error.name === 'TimeoutError'
                      ? 'Validation timed out - file may be too large'
                      : 'Error validating video bitrate',
                },
              });
            })
          )
        ),
        // Collect all results
        toArray(),
        map((results: ValidationResult[]) => {
          const invalidResults = results.filter(item => !item.result.valid);

          if (invalidResults.length === 0) {
            return null;
          }

          // Create error object with details for each invalid file
          const errors: ValidationErrors = {
            invalidBitrate: {
              files: invalidResults.map(item => ({
                name: item.file.name,
                message: item.result.message,
                bitrate: item.result.bitrate,
                targetBitrate: bitrateMbps,
                comparisonType: comparisonType,
                errorMargin: errorMarginMbps,
              })),
            },
          };

          return errors;
        })
      );
    };
  }

  public static passwordMatchValidator(
    passwordField: string = 'password',
    confirmField: string = 'confirmPassword'
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const password = control.get(passwordField);
      const confirmPassword = control.get(confirmField);

      if (!password || !confirmPassword) {
        return null;
      }

      if (password.value === confirmPassword.value) {
        if (confirmPassword.hasError('passwordMismatch')) {
          const errors = { ...confirmPassword.errors };
          delete errors['passwordMismatch'];
          confirmPassword.setErrors(Object.keys(errors).length ? errors : null);
        }
        return null;
      } else {
        confirmPassword.setErrors({ ...confirmPassword.errors, passwordMismatch: true });
        return { passwordMismatch: true };
      }
    };
  }

  public static requireLowercase(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const hasLower = /\p{Ll}/u.test(control.value);
      return hasLower ? null : { requireLowercase: true };
    };
  }

  public static requireUppercase(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const hasUpper = /\p{Lu}/u.test(control.value);
      return hasUpper ? null : { requireUppercase: true };
    };
  }

  public static requireDigit(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const hasDigit = /\p{Nd}/u.test(control.value);
      return hasDigit ? null : { requireDigit: true };
    };
  }

  public static requireSpecial(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const hasSpecial = /[^\p{L}\p{N}]/u.test(control.value);
      return hasSpecial ? null : { requireSpecial: true };
    };
  }
}
